<template>
  <div>
    <v-container class="pt-0 pb-0" fluid>
      <v-row>
        <v-col cols="12">
          <s-toolbar
            no-border
            dark
            label="Reclutamiento - Carga Masiva Multi-Requerimiento por DNI"
            color="#1e3a8a"
          />
        </v-col>
      </v-row>

      <!-- Panel de Control y Selección -->
      <v-row>
        <v-col cols="12">
          <v-card outlined class="rounded-xl elevation-1">
            <v-card-text class="pa-6">
              <v-row align="center" justify="space-between">
                <v-col cols="12" md="6">
                  <div class="d-flex align-center">
                    <v-avatar color="indigo lighten-5" size="48" class="mr-3">
                      <v-icon color="indigo darken-3">mdi-account-multiple-plus</v-icon>
                    </v-avatar>
                    <div>
                      <div class="text-subtitle-1 font-weight-bold indigo--text text--darken-4">
                        Carga Masiva de Postulantes Multi-Requerimiento
                      </div>
                      <div class="text-caption grey--text text--darken-1">
                        Carga e ingresa postulantes (nuevos o existentes) a múltiples requerimientos activos en un solo archivo.
                      </div>
                    </div>
                  </div>
                </v-col>

                <!-- Botones de Descarga y Precarga Inteligente -->
                <v-col cols="12" md="6" class="text-md-right d-flex justify-md-end align-center flex-wrap">
                  <v-btn
                    color="indigo darken-2"
                    outlined
                    large
                    elevation="1"
                    class="rounded-lg font-weight-bold px-4 mr-2 mb-1"
                    @click="openDiagnoseDialog"
                  >
                    <v-icon left color="indigo darken-2">mdi-account-search</v-icon>
                    Pre-cargar por DNIs
                  </v-btn>
                  <v-btn
                    color="indigo darken-3"
                    dark
                    large
                    elevation="2"
                    class="rounded-lg font-weight-bold px-4 mb-1"
                    :loading="downloadingTemplate"
                    @click="downloadMassiveTemplate()"
                  >
                    <v-icon left>mdi-file-excel</v-icon>
                    Plantilla en Blanco (.xlsx)
                  </v-btn>
                  <v-btn
                    icon
                    class="ml-2 mb-1"
                    color="indigo darken-2"
                    :loading="loadingProcesses"
                    title="Actualizar Requerimientos"
                    @click="loadAllApprovedProcesses"
                  >
                    <v-icon>mdi-refresh</v-icon>
                  </v-btn>
                </v-col>
              </v-row>

              <!-- Resumen de Requerimientos Masivos Aprobados Disponibles -->
              <v-row class="mt-3">
                <v-col cols="12">
                  <v-expansion-panels flat class="border rounded-lg">
                    <v-expansion-panel>
                      <v-expansion-panel-header class="indigo lighten-5 py-2">
                        <div class="d-flex align-center">
                          <v-icon color="indigo darken-3" left>mdi-briefcase-check</v-icon>
                          <span class="indigo--text text--darken-4 font-weight-bold text-subtitle-2">
                            Ver Requerimientos Disponibles ({{ approvedProcesses.length }})
                          </span>
                        </div>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content class="pt-4">
                        <v-simple-table dense class="elevation-0">
                          <template v-slot:default>
                            <thead>
                              <tr>
                                <th class="text-center font-weight-bold">ID Requerimiento</th>
                                <th class="font-weight-bold">Campaña / Sede</th>
                                <th class="font-weight-bold">Puesto</th>
                                <th class="text-center font-weight-bold">Vacantes Solicitadas</th>
                                <th class="text-center font-weight-bold">Contratos Activos</th>
                                <th class="text-center font-weight-bold">Vacantes Libres</th>
                                <th class="text-center font-weight-bold">Fecha Fin Contrato</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="item in approvedProcesses" :key="item.ReqID">
                                <td class="text-center">
                                  <v-chip x-small color="indigo" text-color="white" class="font-weight-bold">
                                    {{ item.ReqID }}
                                  </v-chip>
                                </td>
                                <td>
                                  <v-chip x-small color="grey lighten-3" class="font-weight-medium">
                                    {{ item.CmpName || "General" }}
                                  </v-chip>
                                </td>
                                <td class="font-weight-bold indigo--text text--darken-4">
                                  {{ item.PstName }}
                                </td>
                                <td class="text-center">{{ item.ReqQuantity }}</td>
                                <td class="text-center">{{ item.SelectedCount }}</td>
                                <td class="text-center">
                                  <strong :class="item.AvailableSlots > 0 ? 'green--text text--darken-3' : 'red--text'">
                                    {{ item.AvailableSlots }}
                                  </strong>
                                </td>
                                <td class="text-center">{{ formatDate(item.ReqEndContract) }}</td>
                              </tr>
                              <tr v-if="!approvedProcesses.length">
                                <td colspan="7" class="text-center py-4 grey--text">
                                  No hay requerimientos aprobados activos en este momento.
                                </td>
                              </tr>
                            </tbody>
                          </template>
                        </v-simple-table>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-col>
              </v-row>

              <!-- Alerta informativa -->
              <v-alert type="info" outlined dense class="mt-4 rounded-lg">
                <div class="d-flex align-center flex-wrap">
                  <span class="font-weight-medium">
                    📌 <strong>Registro Masivo Multi-Puesto y Nuevas Personas:</strong>
                  </span>
                </div>
                <div class="mt-1 text-caption grey--text text--darken-2">
                  • <strong>Listas Desplegables en Excel:</strong> La plantilla incluye dropdowns automáticos para Puesto/Requerimiento, Tipo de Contrato, Tipo de Costo, Sexo, Turno, Teléfono, Dominio de Correo y Fuente.<br />
                  • <strong>Creación Automática de Personas:</strong> Si la persona no existe previamente, el sistema la creará en <em>GenPerson</em>, <em>GenNaturalPerson</em>, teléfono y correo automáticamente.<br />
                  • <strong>Sueldos y Fechas:</strong> La <strong>FECHA DE INGRESO</strong> se usará para el inicio de labores y automatización del contrato. Si no se especifica sueldo, se tomará el presupuesto del requerimiento.<br />
                  • <strong>Control de Vacantes:</strong> Si las filas exceden las vacantes de un requerimiento específico, solo se rechazan los registros excedentes de dicho requerimiento.
                </div>
              </v-alert>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Importador de Archivo Excel -->
      <v-row>
        <v-col cols="12">
          <v-card outlined class="rounded-xl">
            <v-card-title class="text-subtitle-1 font-weight-bold px-6 pt-4 d-flex align-center justify-space-between">
              <span>Importar archivo Excel de postulantes</span>
              <span class="text-caption grey--text">Sube la plantilla oficial .xlsx</span>
            </v-card-title>
            <v-card-text class="px-6 pb-6">
              <s-import-excel
                v-if="showImporter"
                :headers="excelHeaders"
                @loaded="saveMassive($event)"
              />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Dialog de Resultados de Carga -->
    <v-dialog v-model="resultsDialog" max-width="950px" persistent>
      <v-card class="rounded-xl overflow-hidden">
        <div
          class="px-6 pt-5 pb-4 indigo darken-4 white--text d-flex align-center justify-space-between"
          style="border-bottom: 1px solid rgba(255, 255, 255, 0.12)"
        >
          <div>
            <h3 class="text-h6 font-weight-bold white--text mb-0">
              Resultados de la Carga Masiva
            </h3>
            <span class="text-caption grey--text text--lighten-3"
              >Detalle de postulantes y personas procesadas por requerimiento</span
            >
          </div>
          <v-btn icon dark @click="resultsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="px-6 pt-6">
          <!-- Tarjetas de Resumen -->
          <v-row v-if="summary" class="mb-4" dense>
            <v-col cols="12" sm="4">
              <v-alert
                dense
                color="success lighten-5"
                class="green--text text--darken-3 border mb-0 rounded-lg text-center"
              >
                Insertados / Registrados:
                <strong class="ml-1 text-h6">{{ summary.InsertedCount || summary.insertedCount || 0 }}</strong>
              </v-alert>
            </v-col>
            <v-col cols="12" sm="4">
              <v-alert
                dense
                color="amber lighten-5"
                class="orange--text text--darken-3 border mb-0 rounded-lg text-center"
              >
                Omitidos / Excedentes:
                <strong class="ml-1 text-h6">{{ summary.SkippedCount || summary.skippedCount || 0 }}</strong>
              </v-alert>
            </v-col>
            <v-col cols="12" sm="4">
              <v-alert
                dense
                color="indigo lighten-5"
                class="indigo--text text--darken-4 border mb-0 rounded-lg text-center"
              >
                Total Procesados:
                <strong class="ml-1 text-h6">{{ resultRows.length }}</strong>
              </v-alert>
            </v-col>
          </v-row>

          <v-alert
            v-if="summary && (summary.Message || summary.message)"
            border="left"
            colored-border
            type="info"
            class="elevation-1 mb-4 rounded-lg"
          >
            {{ summary.Message || summary.message }}
          </v-alert>

          <!-- Tabla de Resultados -->
          <v-data-table
            :headers="resultHeaders"
            :items="resultRows"
            dense
            class="border rounded-lg overflow-hidden elevation-0"
            :items-per-page="10"
          >
            <template v-slot:[`item.ReqID`]="{ item }">
              <v-chip v-if="item.ReqID" x-small outlined color="indigo" class="font-weight-bold">
                Req. {{ item.ReqID }}
              </v-chip>
              <span v-else class="grey--text">-</span>
            </template>
            <template v-slot:[`item.Status`]="{ item }">
              <v-chip
                x-small
                :color="statusColor(item.Status)"
                text-color="white"
                class="font-weight-bold"
              >
                {{ item.Status }}
              </v-chip>
            </template>
            <template v-slot:[`item.Message`]="{ item }">
              <span
                :class="
                  item.Status === 'OMITIDO' ||
                  item.Status === 'INVALIDO' ||
                  item.Status === 'RECHAZADO_VACANTES'
                    ? 'red--text text--darken-2 font-weight-medium'
                    : 'green--text text--darken-2'
                "
              >
                {{ item.Message }}
              </span>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions class="px-6 pb-6 pt-2">
          <v-spacer />
          <v-btn
            color="indigo darken-4"
            dark
            class="px-6 rounded-lg font-weight-bold"
            @click="resultsDialog = false"
          >
            Entendido
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog de Diagnóstico y Precarga por DNIs -->
    <v-dialog v-model="diagnoseDialog" max-width="1000px" persistent>
      <v-card class="rounded-xl overflow-hidden">
        <div
          class="px-6 pt-5 pb-4 indigo darken-4 white--text d-flex align-center justify-space-between"
          style="border-bottom: 1px solid rgba(255, 255, 255, 0.12)"
        >
          <div class="d-flex align-center">
            <v-avatar color="indigo lighten-4" size="36" class="mr-3">
              <v-icon color="indigo darken-4">mdi-account-search</v-icon>
            </v-avatar>
            <div>
              <h3 class="text-h6 font-weight-bold white--text mb-0">
                Asistente de Pre-carga Masiva por DNIs
              </h3>
              <span class="text-caption grey--text text--lighten-3">
                Verifica tus postulantes, revisa qué datos faltan y descarga la plantilla oficial con datos pre-cargados
              </span>
            </div>
          </div>
          <v-btn icon dark @click="diagnoseDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="px-6 pt-6">
          <v-row dense>
            <v-col cols="12" md="7">
              <div class="font-weight-bold text-subtitle-2 mb-1 indigo--text text--darken-4">
                1. Pega tu lista de DNIs:
              </div>
              <v-textarea
                v-model="dniInputText"
                rows="5"
                outlined
                dense
                placeholder="Pega aquí los DNIs (uno por línea o separados por coma, espacio o tabulación)..."
                hint="Ej: 74859613, 45871236, 75989315"
                persistent-hint
              />
            </v-col>
            <v-col cols="12" md="5">
              <div class="font-weight-bold text-subtitle-2 mb-1 indigo--text text--darken-4">
                O importa desde archivo:
              </div>
              <v-card outlined class="pa-3 rounded-lg fill-height d-flex flex-column justify-space-between grey lighten-5">
                <div>
                  <div class="text-caption grey--text text--darken-2 mb-2">
                    Sube un archivo (.xlsx o .txt) que tenga la columna de DNIs.
                  </div>
                  <v-file-input
                    v-model="simpleDniFile"
                    accept=".xlsx,.xls,.txt,.csv"
                    label="Seleccionar archivo"
                    dense
                    outlined
                    prepend-icon="mdi-file-document-outline"
                    @change="onSimpleDniFileSelected"
                  />
                </div>
                <div class="text-right">
                  <v-btn
                    text
                    small
                    color="indigo darken-2"
                    class="font-weight-bold px-0 text-caption"
                    @click="downloadSimpleDniTemplate"
                  >
                    <v-icon x-small left>mdi-download</v-icon>
                    Descargar Formato Simple de DNIs (.xlsx)
                  </v-btn>
                </div>
              </v-card>
            </v-col>
          </v-row>

          <v-row class="mt-2" align="center">
            <v-col cols="12" class="text-right">
              <v-btn
                color="indigo darken-3"
                dark
                large
                class="rounded-lg font-weight-bold px-6 elevation-1"
                :loading="loadingDiagnosis"
                @click="runDiagnosis"
              >
                <v-icon left>mdi-database-search</v-icon>
                Consultar y Diagnosticar DNIs
              </v-btn>
            </v-col>
          </v-row>

          <!-- Resultados del Diagnóstico -->
          <div v-if="diagnosisResult" class="mt-4">
            <v-divider class="mb-4" />
            
            <div class="text-subtitle-2 font-weight-bold mb-3 indigo--text text--darken-4 d-flex align-center justify-space-between flex-wrap">
              <span>2. Resumen del Diagnóstico ({{ diagnosisResult.TotalSubmitted }} DNIs):</span>
              <v-btn
                color="success darken-1"
                dark
                class="rounded-lg font-weight-bold px-5"
                @click="downloadPrefilledTemplate"
              >
                <v-icon left>mdi-file-excel</v-icon>
                Descargar Plantilla Oficial Pre-llenada (.xlsx)
              </v-btn>
            </div>

            <!-- Chips de Resumen -->
            <v-row dense class="mb-3">
              <v-col cols="6" sm="4" md="2">
                <v-card outlined class="pa-2 text-center rounded-lg green lighten-5 border">
                  <div class="text-caption green--text text--darken-3 font-weight-bold">Completos</div>
                  <div class="text-h6 green--text text--darken-3 font-weight-black">{{ diagnosisResult.CompleteCount }}</div>
                </v-card>
              </v-col>
              <v-col cols="6" sm="4" md="2">
                <v-card outlined class="pa-2 text-center rounded-lg amber lighten-5 border">
                  <div class="text-caption orange--text text--darken-3 font-weight-bold">Falta Correo</div>
                  <div class="text-h6 orange--text text--darken-3 font-weight-black">{{ diagnosisResult.MissingEmailCount }}</div>
                </v-card>
              </v-col>
              <v-col cols="6" sm="4" md="2">
                <v-card outlined class="pa-2 text-center rounded-lg orange lighten-5 border">
                  <div class="text-caption orange--text text--darken-4 font-weight-bold">Falta Teléfono</div>
                  <div class="text-h6 orange--text text--darken-4 font-weight-black">{{ diagnosisResult.MissingPhoneCount }}</div>
                </v-card>
              </v-col>
              <v-col cols="6" sm="4" md="2">
                <v-card outlined class="pa-2 text-center rounded-lg deep-orange lighten-5 border">
                  <div class="text-caption deep-orange--text text--darken-3 font-weight-bold">Faltan Ambos</div>
                  <div class="text-h6 deep-orange--text text--darken-3 font-weight-black">{{ diagnosisResult.MissingBothCount }}</div>
                </v-card>
              </v-col>
              <v-col cols="6" sm="4" md="2">
                <v-card outlined class="pa-2 text-center rounded-lg indigo lighten-5 border">
                  <div class="text-caption indigo--text text--darken-3 font-weight-bold">Post. Nuevos</div>
                  <div class="text-h6 indigo--text text--darken-3 font-weight-black">{{ diagnosisResult.NewCandidateCount }}</div>
                </v-card>
              </v-col>
              <v-col cols="6" sm="4" md="2">
                <v-card outlined class="pa-2 text-center rounded-lg red lighten-5 border">
                  <div class="text-caption red--text text--darken-3 font-weight-bold">Contrato Activo</div>
                  <div class="text-h6 red--text text--darken-3 font-weight-black">{{ diagnosisResult.ActiveContractCount }}</div>
                </v-card>
              </v-col>
            </v-row>

            <!-- Tabla de Diagnóstico Detallada -->
            <v-data-table
              :headers="diagnosisHeaders"
              :items="diagnosisResult.Items"
              dense
              class="border rounded-lg overflow-hidden elevation-0"
              :items-per-page="5"
            >
              <template v-slot:[`item.DNI`]="{ item }">
                <strong class="font-weight-bold">{{ item.DNI }}</strong>
              </template>
              <template v-slot:[`item.Postulante`]="{ item }">
                <span v-if="item.NOMBRES || item.APELLIDO_PATERNO">
                  {{ item.APELLIDO_PATERNO }} {{ item.APELLIDO_MATERNO }} {{ item.NOMBRES }}
                </span>
                <span v-else class="grey--text font-italic">No registrado en BD (Nuevo)</span>
              </template>
              <template v-slot:[`item.FECHA_NACIMIENTO`]="{ item }">
                <span>{{ formatDate(item.FECHA_NACIMIENTO) }}</span>
              </template>
              <template v-slot:[`item.TELEFONO`]="{ item }">
                <span v-if="item.TELEFONO">{{ item.TELEFONO }}</span>
                <v-chip v-else x-small outlined color="orange darken-3">Sin teléfono</v-chip>
              </template>
              <template v-slot:[`item.CORREO_COMPLETO`]="{ item }">
                <span v-if="item.CORREO_COMPLETO" class="text-caption font-weight-medium">
                  {{ item.CORREO_COMPLETO }}
                </span>
                <v-chip v-else x-small outlined color="orange darken-3">Sin correo</v-chip>
              </template>
              <template v-slot:[`item.ESTADO_DATOS`]="{ item }">
                <v-chip
                  x-small
                  :color="diagnosisStatusColor(item.ESTADO_DATOS)"
                  text-color="white"
                  class="font-weight-bold"
                >
                  {{ item.ESTADO_DATOS }}
                </v-chip>
              </template>
            </v-data-table>
          </div>
        </v-card-text>

        <v-card-actions class="px-6 pb-6 pt-2">
          <v-spacer />
          <v-btn
            outlined
            class="px-5 rounded-lg"
            @click="diagnoseDialog = false"
          >
            Cerrar
          </v-btn>
          <v-btn
            v-if="diagnosisResult && diagnosisResult.Items && diagnosisResult.Items.length"
            color="success darken-1"
            dark
            class="px-6 rounded-lg font-weight-bold"
            @click="downloadPrefilledTemplate"
          >
            <v-icon left>mdi-file-excel</v-icon>
            Descargar Plantilla Pre-llenada (.xlsx)
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <AlertSunshine
      :dialog="loadingDialog"
      text="Procesando registro masivo multi-requerimiento..."
    />
  </div>
</template>

<script>
import AlertSunshine from "@/components/Utils/AlertSunshine.vue";
import HmnRecruitmentProcessService from "@/services/HumanResource/RecruitmentProcess/HmnRecruitmentProcessService";
import DefinitionService from "@/services/General/DefinitionService";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

export default {
  name: "HmnRecruitmentMassiveDni",
  components: {
    AlertSunshine,
  },
  data() {
    return {
      loadingDialog: false,
      loadingProcesses: false,
      loadingCatalogs: false,
      downloadingTemplate: false,
      approvedProcesses: [],
      typeCostOptions: [],
      typeContractOptions: [],
      typeSourceOptions: [],
      typePhoneOptions: [],
      typeDomainEmailOptions: [],
      showImporter: true,
      resultsDialog: false,
      summary: null,
      resultRows: [],
      diagnoseDialog: false,
      loadingDiagnosis: false,
      dniInputText: "",
      simpleDniFile: null,
      diagnosisResult: null,
      diagnosisHeaders: [
        { text: "DNI", value: "DNI", width: 110, align: "center" },
        { text: "Postulante", value: "Postulante", width: 230 },
        { text: "Nacimiento", value: "FECHA_NACIMIENTO", width: 120, align: "center" },
        { text: "Sexo", value: "SEXO", width: 100, align: "center" },
        { text: "Teléfono", value: "TELEFONO", width: 120 },
        { text: "Correo / Dominio", value: "CORREO_COMPLETO", width: 220 },
        { text: "Estado de Datos", value: "ESTADO_DATOS", width: 180, align: "center" },
      ],
      excelHeaders: [
        { column: "DNI", value: "DNI", text: "DNI / DOCUMENTO" },
        { column: "APELLIDO_PATERNO", value: "APELLIDO_PATERNO", text: "APELLIDO PATERNO" },
        { column: "APELLIDO_MATERNO", value: "APELLIDO_MATERNO", text: "APELLIDO MATERNO" },
        { column: "NOMBRES", value: "NOMBRES", text: "NOMBRES" },
        {
          column: "FECHA_NACIMIENTO",
          value: "FECHA_NACIMIENTO",
          text: "FECHA NACIMIENTO",
          date: true,
        },
        { column: "EDAD", value: "EDAD", text: "EDAD" },
        { column: "SEXO", value: "SEXO", text: "SEXO / GÉNERO" },
        { column: "REQUERIMIENTO", value: "REQUERIMIENTO", text: "REQUERIMIENTO / PUESTO" },
        { column: "ID_REQUERIMIENTO", value: "ID_REQUERIMIENTO", text: "ID REQUERIMIENTO" },
        {
          column: "FECHA_INGRESO",
          value: "FECHA_INGRESO",
          text: "FECHA INGRESO (CONTRATO)",
          date: true,
        },
        { column: "TURNO", value: "TURNO", text: "TURNO" },
        { column: "SUELDO", value: "SUELDO", text: "SUELDO" },
        { column: "TIPO_CONTRATO", value: "TIPO_CONTRATO", text: "TIPO CONTRATO" },
        { column: "TIPO_CONTRATO_ID", value: "TIPO_CONTRATO_ID", text: "ID TIPO CONTRATO" },
        { column: "TIPO_COSTO", value: "TIPO_COSTO", text: "TIPO COSTO" },
        { column: "TIPO_COSTO_ID", value: "TIPO_COSTO_ID", text: "ID TIPO COSTO" },
        { column: "TELEFONO", value: "TELEFONO", text: "TELÉFONO" },
        { column: "TIPO_TELEFONO", value: "TIPO_TELEFONO", text: "TIPO TELÉFONO" },
        { column: "CORREO", value: "CORREO", text: "CORREO (USUARIO)" },
        { column: "DOMINIO_CORREO", value: "DOMINIO_CORREO", text: "DOMINIO CORREO" },
        { column: "DOMINIO_ID", value: "DOMINIO_ID", text: "ID DOMINIO" },
        { column: "FUENTE", value: "FUENTE", text: "FUENTE RECLUTAMIENTO" },
        { column: "FUENTE_ID", value: "FUENTE_ID", text: "ID FUENTE" },
      ],
      resultHeaders: [
        { text: "Fila", value: "RowNumber", width: 70, align: "center" },
        { text: "DNI", value: "PrsDocumentNumber", width: 120 },
        { text: "Candidato / Postulante", value: "FullName", width: 240 },
        { text: "Req. ID", value: "ReqID", width: 100, align: "center" },
        { text: "Puesto", value: "PositionName", width: 180 },
        { text: "Estado", value: "Status", width: 140, align: "center" },
        { text: "Detalle / Observación", value: "Message" },
      ],
    };
  },
  async mounted() {
    await Promise.all([
      this.loadAllApprovedProcesses(),
      this.loadCatalogs(),
    ]);
  },
  methods: {
    formatDate(dateVal) {
      if (!dateVal) return "-";
      try {
        const d = new Date(dateVal);
        if (isNaN(d.getTime())) return "-";
        return d.toLocaleDateString("es-PE");
      } catch {
        return "-";
      }
    },
    formatReqDisplay(p) {
      if (!p) return "";
      const cmp = p.CmpName ? `${p.CmpName} - ` : "";
      return `${cmp}${p.PstName} [Req. ${p.ReqID}]`;
    },
    async loadCatalogs() {
      this.loadingCatalogs = true;
      try {
        const [rCost, rContract, rSource, rPhone, rEmail] = await Promise.all([
          DefinitionService.definitiongroup(1045, 0, this.$fun.getUserID()),
          DefinitionService.definitiongroup(1069, 0, this.$fun.getUserID()),
          DefinitionService.definitiongroup(1521, 0, this.$fun.getUserID()),
          DefinitionService.definitiongroup(1111, 0, this.$fun.getUserID()),
          DefinitionService.definitiongroup(1518, 0, this.$fun.getUserID()),
        ]);
        this.typeCostOptions = rCost?.data || [];
        this.typeContractOptions = rContract?.data || [];
        this.typeSourceOptions = rSource?.data || [];
        this.typePhoneOptions = rPhone?.data || [];
        this.typeDomainEmailOptions = rEmail?.data || [];
      } catch (error) {
        console.error("Error al cargar catálogos:", error);
      } finally {
        this.loadingCatalogs = false;
      }
    },
    async loadAllApprovedProcesses() {
      this.loadingProcesses = true;
      try {
        const response = await HmnRecruitmentProcessService.activeMassiveProcesses(
          0,
          this.$fun.getUserID(),
          1, // 1 = Profesional / Empleados
        );
        this.approvedProcesses = response?.data || [];
      } catch (error) {
        this.$fun.alert(
          "Error al cargar los requerimientos aprobados disponibles",
          "error",
        );
      } finally {
        this.loadingProcesses = false;
      }
    },
    statusColor(status) {
      switch (status) {
        case "INSERTADO":
          return "success";
        case "YA_REGISTRADO":
        case "DUPLICADO":
          return "warning";
        case "RECHAZADO_VACANTES":
          return "orange darken-3";
        case "OMITIDO":
        case "INVALIDO":
          return "error";
        default:
          return "grey";
      }
    },
    openDiagnoseDialog() {
      this.diagnoseDialog = true;
    },
    diagnosisStatusColor(status) {
      switch (status) {
        case "COMPLETO":
          return "success";
        case "FALTA CORREO":
          return "amber darken-2";
        case "FALTA TELÉFONO":
          return "orange darken-2";
        case "FALTA TELÉFONO Y CORREO":
          return "orange darken-4";
        case "FALTA FECHA NACIMIENTO":
          return "deep-orange";
        case "POSTULANTE NUEVO":
          return "indigo lighten-1";
        case "TIENE CONTRATO ACTIVO":
          return "red darken-2";
        default:
          return "grey";
      }
    },
    async runDiagnosis() {
      const raw = String(this.dniInputText || "").trim();
      if (!raw) {
        this.$fun.alert("Ingresa o pega al menos un DNI para diagnosticar", "warning");
        return;
      }
      const cleanList = raw
        .split(/[\r\n,;\t ]+/)
        .map((x) => {
          let s = x.trim().replace(/[^0-9A-Za-z]/g, "");
          if (/^\d{1,8}$/.test(s)) s = s.padStart(8, "0");
          return s;
        })
        .filter((x) => x.length >= 8);

      if (!cleanList.length) {
        this.$fun.alert("No se encontraron DNIs válidos (mínimo 8 caracteres)", "warning");
        return;
      }

      this.loadingDiagnosis = true;
      try {
        const payload = {
          Dnis: cleanList,
        };
        const response = await HmnRecruitmentProcessService.diagnoseDnis(
          payload,
          this.$fun.getUserID(),
        );
        this.diagnosisResult = response?.data || null;
        if (!this.diagnosisResult?.Items?.length) {
          this.$fun.alert("No se obtuvieron resultados para los DNIs enviados", "info");
        } else {
          this.$fun.alert(
            `Diagnóstico completado: ${this.diagnosisResult.TotalSubmitted} DNIs evaluados`,
            "success",
          );
        }
      } catch (error) {
        console.error("Error al diagnosticar los DNIs:", error);
        const serverMsg =
          (typeof error?.response?.data === "string" ? error.response.data : null) ||
          error?.response?.data?.Message ||
          error?.response?.data?.message ||
          error?.message ||
          "Error al diagnosticar los DNIs";
        this.$fun.alert(serverMsg, "error");
      } finally {
        this.loadingDiagnosis = false;
      }
    },
    async downloadPrefilledTemplate() {
      if (!this.diagnosisResult?.Items?.length) {
        this.$fun.alert("Primero debes ejecutar el diagnóstico de DNIs", "warning");
        return;
      }
      await this.downloadMassiveTemplate(this.diagnosisResult.Items);
    },
    async downloadSimpleDniTemplate() {
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("LISTA_DNIS", { views: [{ showGridLines: true }] });
      ws.columns = [{ header: "DNI", key: "DNI", width: 18 }];
      const hRow = ws.getRow(1);
      hRow.height = 26;
      hRow.eachCell((cell) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1E3A8A" } };
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.alignment = { vertical: "middle", horizontal: "center" };
      });
      ws.addRow({ DNI: "74859613" });
      ws.addRow({ DNI: "45871236" });
      const buffer = await wb.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), "Plantilla_Simple_Lista_DNIs.xlsx");
    },
    async onSimpleDniFileSelected(file) {
      if (!file) return;
      try {
        if (file.name.endsWith(".txt") || file.name.endsWith(".csv")) {
          const text = await file.text();
          const extracted = text
            .split(/[\r\n,;]+/)
            .map((x) => {
              let s = x.trim().replace(/[^0-9A-Za-z]/g, "");
              if (/^\d{1,8}$/.test(s)) s = s.padStart(8, "0");
              return s;
            })
            .filter((x) => x.length >= 8);
          this.dniInputText = extracted.join("\n");
          this.$fun.alert(`Se extrajeron ${extracted.length} DNIs del archivo`, "success");
          return;
        }
        const arrayBuffer = await file.arrayBuffer();
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(arrayBuffer);
        const worksheet = workbook.worksheets[0];
        const dnis = [];
        worksheet.eachRow((row, rowNumber) => {
          if (rowNumber === 1 && isNaN(Number(row.getCell(1).value))) return;
          let val = String(row.getCell(1).value || "").replace(/[^0-9A-Za-z]/g, "").trim();
          if (/^\d{1,8}$/.test(val)) {
            val = val.padStart(8, "0");
          }
          if (val.length >= 8 && !dnis.includes(val)) {
            dnis.push(val);
          }
        });
        this.dniInputText = dnis.join("\n");
        this.$fun.alert(`Se extrajeron ${dnis.length} DNIs de la hoja de cálculo`, "success");
      } catch (e) {
        this.$fun.alert("Error al leer el archivo de DNIs: " + e.message, "error");
      }
    },
    async downloadMassiveTemplate(prefilledItems = null) {
      this.downloadingTemplate = true;
      try {
        if (!this.approvedProcesses.length) {
          await this.loadAllApprovedProcesses();
        }

        const workbook = new ExcelJS.Workbook();
        workbook.creator = "SunExpert - Reclutamiento Masivo";
        workbook.created = new Date();

        // 1. Hoja Principal: CARGA_MASIVA
        const wsMain = workbook.addWorksheet("CARGA_MASIVA", {
          views: [{ showGridLines: true }],
        });

        wsMain.columns = [
          { header: "DNI", key: "DNI", width: 14 },
          { header: "APELLIDO_PATERNO", key: "APELLIDO_PATERNO", width: 20 },
          { header: "APELLIDO_MATERNO", key: "APELLIDO_MATERNO", width: 20 },
          { header: "NOMBRES", key: "NOMBRES", width: 22 },
          { header: "FECHA_NACIMIENTO", key: "FECHA_NACIMIENTO", width: 18 },
          { header: "EDAD", key: "EDAD", width: 10 },
          { header: "SEXO", key: "SEXO", width: 14 },
          { header: "REQUERIMIENTO", key: "REQUERIMIENTO", width: 36 },
          { header: "ID_REQUERIMIENTO", key: "ID_REQUERIMIENTO", width: 18, hidden: true },
          { header: "FECHA_INGRESO", key: "FECHA_INGRESO", width: 20 },
          { header: "TURNO", key: "TURNO", width: 14 },
          { header: "SUELDO", key: "SUELDO", width: 14 },
          { header: "TIPO_CONTRATO", key: "TIPO_CONTRATO", width: 22 },
          { header: "TIPO_CONTRATO_ID", key: "TIPO_CONTRATO_ID", width: 16, hidden: true },
          { header: "TIPO_COSTO", key: "TIPO_COSTO", width: 20 },
          { header: "TIPO_COSTO_ID", key: "TIPO_COSTO_ID", width: 16, hidden: true },
          { header: "TELEFONO", key: "TELEFONO", width: 16 },
          { header: "TIPO_TELEFONO", key: "TIPO_TELEFONO", width: 18 },
          { header: "CORREO", key: "CORREO", width: 20 },
          { header: "DOMINIO_CORREO", key: "DOMINIO_CORREO", width: 22 },
          { header: "DOMINIO_ID", key: "DOMINIO_ID", width: 14, hidden: true },
          { header: "FUENTE", key: "FUENTE", width: 22 },
          { header: "FUENTE_ID", key: "FUENTE_ID", width: 14, hidden: true },
        ];

        // Estilos del encabezado principal
        const headerRow = wsMain.getRow(1);
        headerRow.height = 32;
        headerRow.eachCell((cell) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF1E3A8A" }, // Indigo Corporativo
          };
          cell.font = {
            bold: true,
            color: { argb: "FFFFFFFF" },
            size: 11,
          };
          cell.alignment = { vertical: "middle", horizontal: "center" };
          cell.border = {
            top: { style: "thin", color: { argb: "FFCCCCCC" } },
            left: { style: "thin", color: { argb: "FFCCCCCC" } },
            bottom: { style: "medium", color: { argb: "FF1E3A8A" } },
            right: { style: "thin", color: { argb: "FFCCCCCC" } },
          };
        });

        // 2. Hoja de Soporte: REQUERIMIENTOS_APROBADOS
        const wsReq = workbook.addWorksheet("REQUERIMIENTOS_APROBADOS", {
          views: [{ showGridLines: true }],
        });

        wsReq.columns = [
          { header: "ID_REQUERIMIENTO", key: "ReqID", width: 18 },
          { header: "CAMPAÑA_PUESTO", key: "DisplayName", width: 44 },
          { header: "PUESTO", key: "PstName", width: 30 },
          { header: "CAMPAÑA", key: "CmpName", width: 24 },
          { header: "VACANTES SOLICITADAS", key: "ReqQuantity", width: 20 },
          { header: "CONTRATOS ACTIVOS", key: "SelectedCount", width: 18 },
          { header: "VACANTES LIBRES", key: "AvailableSlots", width: 18 },
          { header: "FECHA_FIN_CONTRATO", key: "ReqEndContract", width: 20 },
        ];

        const reqHeaderRow = wsReq.getRow(1);
        reqHeaderRow.height = 28;
        reqHeaderRow.eachCell((cell) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF1E3A8A" },
          };
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
          cell.alignment = { vertical: "middle", horizontal: "center" };
        });

        this.approvedProcesses.forEach((p) => {
          wsReq.addRow({
            ReqID: p.ReqID,
            DisplayName: this.formatReqDisplay(p),
            PstName: p.PstName,
            CmpName: p.CmpName || "General",
            ReqQuantity: p.ReqQuantity,
            SelectedCount: p.SelectedCount,
            AvailableSlots: p.AvailableSlots,
            ReqEndContract: this.formatDate(p.ReqEndContract),
          });
        });

        // 3. Hoja de Soporte: CATALOGOS_SISTEMA
        const wsCat = workbook.addWorksheet("CATALOGOS_SISTEMA", {
          views: [{ showGridLines: true }],
        });

        wsCat.columns = [
          { header: "CONTRATO_ID", key: "CtrID", width: 14 },
          { header: "TIPO_CONTRATO", key: "CtrName", width: 24 },
          { header: "COSTO_ID", key: "CosID", width: 14 },
          { header: "TIPO_COSTO", key: "CosName", width: 20 },
          { header: "DOMINIO_ID", key: "DomID", width: 14 },
          { header: "DOMINIO_CORREO", key: "DomName", width: 22 },
          { header: "TELEFONO_ID", key: "TelID", width: 14 },
          { header: "TIPO_TELEFONO", key: "TelName", width: 20 },
          { header: "FUENTE_ID", key: "FueID", width: 14 },
          { header: "TIPO_FUENTE", key: "FueName", width: 22 },
          { header: "TURNO_ID", key: "TurID", width: 12 },
          { header: "TURNO_NOMBRE", key: "TurName", width: 16 },
        ];

        const catHeaderRow = wsCat.getRow(1);
        catHeaderRow.height = 28;
        catHeaderRow.eachCell((cell) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF374151" },
          };
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
          cell.alignment = { vertical: "middle", horizontal: "center" };
        });

        const maxRows = Math.max(
          this.typeContractOptions.length,
          this.typeCostOptions.length,
          this.typeDomainEmailOptions.length,
          this.typePhoneOptions.length,
          this.typeSourceOptions.length,
          2,
        );

        const turns = [
          { id: 1, name: "Diurno" },
          { id: 2, name: "Nocturno" },
        ];

        for (let i = 0; i < maxRows; i++) {
          const ctr = this.typeContractOptions[i];
          const cos = this.typeCostOptions[i];
          const dom = this.typeDomainEmailOptions[i];
          const tel = this.typePhoneOptions[i];
          const fue = this.typeSourceOptions[i];
          const tur = turns[i];

          wsCat.addRow({
            CtrID: ctr ? ctr.DedValue : "",
            CtrName: ctr ? ctr.DedDescription : "",
            CosID: cos ? cos.DedValue : "",
            CosName: cos ? cos.DedDescription : "",
            DomID: dom ? dom.DedValue : "",
            DomName: dom ? dom.DedDescription : "",
            TelID: tel ? tel.DedValue : "",
            TelName: tel ? tel.DedDescription : "",
            FueID: fue ? fue.DedValue : "",
            FueName: fue ? fue.DedDescription : "",
            TurID: tur ? tur.id : "",
            TurName: tur ? tur.name : "",
          });
        }

        const lastReqRow = Math.max(this.approvedProcesses.length + 1, 2);
        const lastCtrRow = Math.max(this.typeContractOptions.length + 1, 2);
        const lastCosRow = Math.max(this.typeCostOptions.length + 1, 2);
        const lastDomRow = Math.max(this.typeDomainEmailOptions.length + 1, 2);
        const lastTelRow = Math.max(this.typePhoneOptions.length + 1, 2);
        const lastFueRow = Math.max(this.typeSourceOptions.length + 1, 2);

        // Filas de Ejemplo de Carga en Hoja 1
        const exampleReq1 = this.approvedProcesses[0] || {
          ReqID: 101,
          PstName: "Operario de Campo",
          CmpName: "Campaña Arándano",
        };
        const exampleReq2 = this.approvedProcesses[1] || exampleReq1;
        const exampleCtr = this.typeContractOptions[0]?.DedDescription || "INTERMITENTE";
        const exampleCos = this.typeCostOptions[0]?.DedDescription || "MANO DE OBRA DIRECTA";
        const exampleDom = this.typeDomainEmailOptions[0]?.DedDescription || "@gmail.com";
        const exampleSource = this.typeSourceOptions[0]?.DedDescription || "Bolsa de Trabajo";

        if (prefilledItems && prefilledItems.length > 0) {
          prefilledItems.forEach((item) => {
            const addedRow = wsMain.addRow({
              DNI: item.DNI || "",
              APELLIDO_PATERNO: item.APELLIDO_PATERNO || "",
              APELLIDO_MATERNO: item.APELLIDO_MATERNO || "",
              NOMBRES: item.NOMBRES || "",
              FECHA_NACIMIENTO: item.FECHA_NACIMIENTO || "",
              SEXO: item.SEXO || "Masculino",
              REQUERIMIENTO: "",
              FECHA_INGRESO: "",
              TURNO: "Diurno",
              SUELDO: null,
              TIPO_CONTRATO: exampleCtr,
              TIPO_COSTO: exampleCos,
              TELEFONO: item.TELEFONO || "",
              TIPO_TELEFONO: "Celular",
              CORREO: item.CORREO_USUARIO || "",
              DOMINIO_CORREO: item.DOMINIO_DESCRIPCION || exampleDom,
              FUENTE: exampleSource,
            });

            if (item.TIENE_CONTRATO_ACTIVO) {
              addedRow.getCell("DNI").note =
                "ALERTA: Esta persona ya tiene un Contrato Activo en el sistema.";
              addedRow.getCell("DNI").fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFFEE2E2" },
              };
            }
          });
        } else {
          // Agregar filas de ejemplo
          wsMain.addRow({
            DNI: "74859613",
            APELLIDO_PATERNO: "PEREZ",
            APELLIDO_MATERNO: "QUISPE",
            NOMBRES: "JUAN CARLOS",
            FECHA_NACIMIENTO: "1998-04-15",
            SEXO: "Masculino",
            REQUERIMIENTO: this.formatReqDisplay(exampleReq1),
            FECHA_INGRESO: "2026-10-15",
            TURNO: "Diurno",
            SUELDO: 1130,
            TIPO_CONTRATO: exampleCtr,
            TIPO_COSTO: exampleCos,
            TELEFONO: "987654321",
            TIPO_TELEFONO: "Celular",
            CORREO: "juan.perez",
            DOMINIO_CORREO: exampleDom,
            FUENTE: exampleSource,
          });

          wsMain.addRow({
            DNI: "45871236",
            APELLIDO_PATERNO: "GOMEZ",
            APELLIDO_MATERNO: "FLORES",
            NOMBRES: "MARIA ELENA",
            FECHA_NACIMIENTO: "2001-08-20",
            SEXO: "Femenino",
            REQUERIMIENTO: this.formatReqDisplay(exampleReq2),
            FECHA_INGRESO: "2026-10-15",
            TURNO: "Nocturno",
            SUELDO: 1130,
            TIPO_CONTRATO: exampleCtr,
            TIPO_COSTO: exampleCos,
            TELEFONO: "912345678",
            TIPO_TELEFONO: "Celular",
            CORREO: "maria.gomez",
            DOMINIO_CORREO: exampleDom,
            FUENTE: exampleSource,
          });
        }

        const totalTemplateRows =
          prefilledItems && prefilledItems.length > 0
            ? Math.max(prefilledItems.length + 1, 100)
            : 250;

        // Configurar Fórmulas y Validación de Datos (Dropdowns)
        for (let rowIdx = 2; rowIdx <= totalTemplateRows; rowIdx++) {
          // Fórmula EDAD (Col F) basada en FECHA_NACIMIENTO (Col E)
          wsMain.getCell(`F${rowIdx}`).value = {
            formula: `=IF(E${rowIdx}="","",INT((TODAY()-E${rowIdx})/365.25))`,
          };

          // Fórmula ID_REQUERIMIENTO (Col I) basada en REQUERIMIENTO (Col H)
          wsMain.getCell(`I${rowIdx}`).value = {
            formula: `=IF(H${rowIdx}="","",IFERROR(INDEX(REQUERIMIENTOS_APROBADOS!$A$2:$A$${lastReqRow},MATCH(H${rowIdx},REQUERIMIENTOS_APROBADOS!$B$2:$B$${lastReqRow},0)),""))`,
          };

          // Fórmula TIPO_CONTRATO_ID (Col N) basada en TIPO_CONTRATO (Col M)
          wsMain.getCell(`N${rowIdx}`).value = {
            formula: `=IF(M${rowIdx}="","",IFERROR(INDEX(CATALOGOS_SISTEMA!$A$2:$A$${lastCtrRow},MATCH(M${rowIdx},CATALOGOS_SISTEMA!$B$2:$B$${lastCtrRow},0)),""))`,
          };

          // Fórmula TIPO_COSTO_ID (Col P) basada en TIPO_COSTO (Col O)
          wsMain.getCell(`P${rowIdx}`).value = {
            formula: `=IF(O${rowIdx}="","",IFERROR(INDEX(CATALOGOS_SISTEMA!$C$2:$C$${lastCosRow},MATCH(O${rowIdx},CATALOGOS_SISTEMA!$D$2:$D$${lastCosRow},0)),""))`,
          };

          // Fórmula DOMINIO_ID (Col U) basada en DOMINIO_CORREO (Col T)
          wsMain.getCell(`U${rowIdx}`).value = {
            formula: `=IF(T${rowIdx}="","",IFERROR(INDEX(CATALOGOS_SISTEMA!$E$2:$E$${lastDomRow},MATCH(T${rowIdx},CATALOGOS_SISTEMA!$F$2:$F$${lastDomRow},0)),""))`,
          };

          // Fórmula FUENTE_ID (Col W) basada en FUENTE (Col V)
          wsMain.getCell(`W${rowIdx}`).value = {
            formula: `=IF(V${rowIdx}="","",IFERROR(INDEX(CATALOGOS_SISTEMA!$I$2:$I$${lastFueRow},MATCH(V${rowIdx},CATALOGOS_SISTEMA!$J$2:$J$${lastFueRow},0)),""))`,
          };

          // Sexo (Col G)
          wsMain.getCell(`G${rowIdx}`).dataValidation = {
            type: "list",
            allowBlank: true,
            formulae: ['"Masculino,Femenino"'],
          };

          // Requerimiento (Col H)
          if (this.approvedProcesses.length > 0) {
            wsMain.getCell(`H${rowIdx}`).dataValidation = {
              type: "list",
              allowBlank: true,
              formulae: [`REQUERIMIENTOS_APROBADOS!$B$2:$B$${lastReqRow}`],
            };
          }

          // Turno (Col K)
          wsMain.getCell(`K${rowIdx}`).dataValidation = {
            type: "list",
            allowBlank: true,
            formulae: ['"Diurno,Nocturno"'],
          };

          // Tipo Contrato (Col M)
          if (this.typeContractOptions.length > 0) {
            wsMain.getCell(`M${rowIdx}`).dataValidation = {
              type: "list",
              allowBlank: true,
              formulae: [`CATALOGOS_SISTEMA!$B$2:$B$${lastCtrRow}`],
            };
          }

          // Tipo Costo (Col O)
          if (this.typeCostOptions.length > 0) {
            wsMain.getCell(`O${rowIdx}`).dataValidation = {
              type: "list",
              allowBlank: true,
              formulae: [`CATALOGOS_SISTEMA!$D$2:$D$${lastCosRow}`],
            };
          }

          // Tipo Teléfono (Col R)
          if (this.typePhoneOptions.length > 0) {
            wsMain.getCell(`R${rowIdx}`).dataValidation = {
              type: "list",
              allowBlank: true,
              formulae: [`CATALOGOS_SISTEMA!$H$2:$H$${lastTelRow}`],
            };
          }

          // Dominio Correo (Col T)
          if (this.typeDomainEmailOptions.length > 0) {
            wsMain.getCell(`T${rowIdx}`).dataValidation = {
              type: "list",
              allowBlank: true,
              formulae: [`CATALOGOS_SISTEMA!$F$2:$F$${lastDomRow}`],
            };
          }

          // Fuente (Col V)
          if (this.typeSourceOptions.length > 0) {
            wsMain.getCell(`V${rowIdx}`).dataValidation = {
              type: "list",
              allowBlank: true,
              formulae: [`CATALOGOS_SISTEMA!$J$2:$J$${lastFueRow}`],
            };
          }
        }

        // Descargar Buffer
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const outFileName =
          prefilledItems && prefilledItems.length > 0
            ? `Plantilla_Masiva_Prellenada_DNI_${new Date().toISOString().substring(0, 10)}.xlsx`
            : `Plantilla_Reclutamiento_Masivo_MultiRequerimiento_${new Date().toISOString().substring(0, 10)}.xlsx`;
        saveAs(blob, outFileName);

        this.$fun.alert(
          "Plantilla Excel generada con éxito con IDs automatizados, fórmulas y listas desplegables",
          "success",
        );
      } catch (error) {
        console.error("Error al generar plantilla Excel:", error);
        this.$fun.alert("Error al generar la plantilla Excel", "error");
      } finally {
        this.downloadingTemplate = false;
      }
    },
    async saveMassive(items) {
      const parseTurn = (val) => {
        if (!val) return 1;
        const norm = String(val).trim().toLowerCase();
        if (
          norm === "1" ||
          norm.includes("diurno") ||
          norm.includes("dia") ||
          norm === "día" ||
          norm === "d"
        )
          return 1;
        if (
          norm === "2" ||
          norm.includes("nocturno") ||
          norm.includes("noche") ||
          norm === "n"
        )
          return 2;
        return 1;
      };

      const parseSex = (val) => {
        if (!val) return 1;
        const s = String(val).trim().toUpperCase();
        if (s === "2" || s.startsWith("F") || s.includes("FEM")) return 2;
        return 1; // 1 = Masculino
      };

      const resolveReqID = (rawReq, rawPst) => {
        if (rawReq != null && rawReq !== "" && !isNaN(Number(rawReq))) {
          return Number(rawReq);
        }
        const searchStr = String(rawReq || rawPst || "").trim();
        if (!searchStr) return null;

        // Si contiene el tag [Req. X] o Req. X, extraer directamente el ID
        const reqMatch = searchStr.match(/req\.?\s*(\d+)/i);
        if (reqMatch && reqMatch[1]) {
          const extractedId = Number(reqMatch[1]);
          const found = this.approvedProcesses.find((p) => p.ReqID === extractedId);
          if (found) return found.ReqID;
        }

        const lower = searchStr.toLowerCase();
        const match = this.approvedProcesses.find((p) => {
          const cmpPart = p.CmpName ? `${p.CmpName.toLowerCase()} - ` : "";
          const fullLabel = `${cmpPart}${p.PstName.toLowerCase()}`;
          const fullWithReq = `${fullLabel} [req. ${p.ReqID}]`;

          return (
            p.ReqID == searchStr ||
            fullWithReq === lower ||
            fullLabel === lower ||
            p.PstName.toLowerCase() === lower ||
            fullWithReq.includes(lower) ||
            lower.includes(fullLabel)
          );
        });
        return match ? match.ReqID : null;
      };

      const resolveContractType = (rawType, rawId) => {
        if (rawId != null && rawId !== "" && !isNaN(Number(rawId))) {
          return Number(rawId);
        }
        if (rawType != null && rawType !== "" && !isNaN(Number(rawType))) {
          return Number(rawType);
        }
        const text = String(rawType || "").trim().toLowerCase();
        if (!text) return 11;
        const match = this.typeContractOptions.find(
          (c) =>
            c.DedDescription.toLowerCase() === text ||
            c.DedDescription.toLowerCase().includes(text) ||
            text.includes(c.DedDescription.toLowerCase()),
        );
        return match ? match.DedValue : 11;
      };

      const resolveCostType = (rawType, rawId) => {
        if (rawId != null && rawId !== "" && !isNaN(Number(rawId))) {
          return Number(rawId);
        }
        if (rawType != null && rawType !== "" && !isNaN(Number(rawType))) {
          return Number(rawType);
        }
        const text = String(rawType || "").trim().toLowerCase();
        if (!text) return 2;
        const match = this.typeCostOptions.find(
          (c) =>
            c.DedDescription.toLowerCase() === text ||
            c.DedDescription.toLowerCase().includes(text) ||
            text.includes(c.DedDescription.toLowerCase()),
        );
        return match ? match.DedValue : 2;
      };

      const resolveDomainEmail = (rawDom, rawDomId) => {
        if (rawDomId != null && rawDomId !== "" && !isNaN(Number(rawDomId))) {
          return Number(rawDomId);
        }
        if (rawDom != null && rawDom !== "" && !isNaN(Number(rawDom))) {
          return Number(rawDom);
        }
        const text = String(rawDom || "").trim().toLowerCase();
        if (!text) return 1;
        const match = this.typeDomainEmailOptions.find(
          (d) =>
            d.DedDescription.toLowerCase() === text ||
            d.DedDescription.toLowerCase().includes(text) ||
            text.includes(d.DedDescription.toLowerCase()),
        );
        return match ? match.DedValue : 1;
      };

      const resolvePhoneType = (rawType) => {
        if (rawType != null && rawType !== "" && !isNaN(Number(rawType))) {
          return Number(rawType);
        }
        const text = String(rawType || "").trim().toLowerCase();
        if (!text) return 1;
        const match = this.typePhoneOptions.find(
          (t) =>
            t.DedDescription.toLowerCase() === text ||
            t.DedDescription.toLowerCase().includes(text) ||
            text.includes(t.DedDescription.toLowerCase()),
        );
        return match ? match.DedValue : 1;
      };

      const resolveSource = (rawSource, rawSourceId) => {
        if (rawSourceId != null && rawSourceId !== "" && !isNaN(Number(rawSourceId))) {
          return Number(rawSourceId);
        }
        if (rawSource != null && rawSource !== "" && !isNaN(Number(rawSource))) {
          return Number(rawSource);
        }
        const text = String(rawSource || "").trim().toLowerCase();
        if (!text) return 2;
        const match = this.typeSourceOptions.find(
          (f) =>
            f.DedDescription.toLowerCase() === text ||
            f.DedDescription.toLowerCase().includes(text) ||
            text.includes(f.DedDescription.toLowerCase()),
        );
        return match ? match.DedValue : 2;
      };

      const todayIso = new Date().toISOString().substring(0, 10);

      // Filtrar filas no vacías
      const rawRows = (items || []).filter((item) => {
        const dni = String(item?.DNI || item?.DOCUMENTO || item?.PrsDocumentNumber || "").trim();
        const nom = String(item?.NOMBRES || item?.NOMBRE || "").trim();
        const ape = String(item?.APELLIDO_PATERNO || item?.PATERNO || "").trim();
        return dni !== "" || nom !== "" || ape !== "";
      });

      if (!rawRows.length) {
        this.$fun.alert("El archivo no contiene ningún registro para procesar", "warning");
        this.resetImporter();
        return;
      }

      // Validar Fecha de Ingreso (obligatoria para inicio de contrato)
      const missingIngresoRows = [];
      rawRows.forEach((item, index) => {
        const rowNum = index + 2;
        const fechaIngreso = item?.FECHA_INGRESO || item?.CprStartDate;
        if (!fechaIngreso) {
          missingIngresoRows.push(rowNum);
        }
      });

      if (missingIngresoRows.length > 0) {
        const invalidList = missingIngresoRows.slice(0, 8).join(", ");
        this.$fun.alert(
          `La FECHA DE INGRESO (inicio de labores) es obligatoria. Faltan en la(s) fila(s): ${invalidList}${missingIngresoRows.length > 8 ? "..." : ""}`,
          "warning",
        );
        this.resetImporter();
        return;
      }

      const rows = rawRows.map((item, index) => {
        const reqID = resolveReqID(
          item?.ID_REQUERIMIENTO || item?.ReqID,
          item?.REQUERIMIENTO || item?.PUESTO,
        );

        const birthDate = item?.FECHA_NACIMIENTO || null;
        let ageVal = item?.EDAD != null && item.EDAD !== "" && !isNaN(Number(item.EDAD)) ? Number(item.EDAD) : null;
        if (!ageVal && birthDate) {
          const bd = new Date(birthDate);
          if (!isNaN(bd.getTime())) {
            ageVal = Math.floor((new Date() - bd) / (365.25 * 24 * 60 * 60 * 1000));
          }
        }

        const invDate = item?.FECHA_INGRESO || todayIso;

        return {
          RowNumber: index + 2,
          PrsDocumentNumber: String(item?.DNI || item?.DOCUMENTO || item?.PrsDocumentNumber || "")
            .replace(/[^0-9A-Za-z]/g, "")
            .trim(),
          NtpPaternalSurname: String(item?.APELLIDO_PATERNO || item?.PATERNO || "").trim(),
          NtpMaternalSurname: String(item?.APELLIDO_MATERNO || item?.MATERNO || "").trim(),
          NtpName: String(item?.NOMBRES || item?.NOMBRE || "").trim(),
          NtpBirthDate: birthDate,
          TypeSex: parseSex(item?.SEXO || item?.GENERO),
          PadAddress: "",
          ReqID: reqID,
          InvitationDate: invDate,
          AppointmentDate: invDate,
          CprStartDate: item?.FECHA_INGRESO || todayIso,
          TypeTurn: parseTurn(item?.TURNO),
          CprSalary:
            item?.SUELDO != null && item.SUELDO !== "" && !isNaN(Number(item.SUELDO))
              ? Number(item.SUELDO)
              : null,
          TypeContract: resolveContractType(item?.TIPO_CONTRATO, item?.TIPO_CONTRATO_ID),
          TypeCost: resolveCostType(item?.TIPO_COSTO, item?.TIPO_COSTO_ID),
          SourceID: resolveSource(item?.FUENTE || item?.TIPO_FUENTE, item?.FUENTE_ID),
          Age: ageVal,
          Phone: item?.TELEFONO
            ? String(item.TELEFONO).replace(/[^0-9]/g, "")
            : null,
          TypePhone: resolvePhoneType(item?.TIPO_TELEFONO),
          EmailUser: item?.CORREO ? String(item.CORREO).trim() : null,
          TypeDomainEmail: resolveDomainEmail(
            item?.DOMINIO_CORREO,
            item?.DOMINIO_ID,
          ),
        };
      });

      // Validar requerimientos obligatorios
      const missingReqRows = rows.filter((r) => r.PrsDocumentNumber && (!r.ReqID || r.ReqID <= 0));
      if (missingReqRows.length > 0) {
        const invalidList = missingReqRows.map((r) => r.RowNumber).slice(0, 10).join(", ");
        this.$fun.alert(
          `Las siguientes filas no tienen un ID_REQUERIMIENTO o REQUERIMIENTO válido: ${invalidList}. Revisa la hoja de requerimientos disponibles.`,
          "warning",
        );
        this.resetImporter();
        return;
      }

      this.loadingDialog = true;
      try {
        const payload = {
          MrcID: 0,
          Rows: rows,
        };

        const response = await HmnRecruitmentProcessService.massiveByDniMassive(
          payload,
          0,
          this.$fun.getUserID(),
        );

        this.summary = response?.data || null;

        const rawResultRows = this.summary?.Rows || this.summary?.rows || [];
        this.resultRows = rawResultRows.map((r) => ({
          RowNumber: r.RowNumber ?? r.rowNumber ?? 0,
          PrsDocumentNumber: r.PrsDocumentNumber ?? r.prsDocumentNumber ?? "",
          FullName: r.FullName ?? r.fullName ?? "",
          ReqID: r.ReqID ?? r.reqID ?? null,
          PositionName: r.PositionName ?? r.positionName ?? "Puesto",
          Status: r.Status ?? r.status ?? "",
          Message: r.Message ?? r.message ?? "",
        }));

        this.resultsDialog = true;

        const msg = this.summary?.Message ?? this.summary?.message ?? "";
        this.$fun.alert(
          msg || "Carga masiva finalizada correctamente",
          "success",
        );

        // Recargar los contadores actualizados de requerimientos aprobados
        await this.loadAllApprovedProcesses();
      } catch (error) {
        this.$fun.alert(
          error?.response?.data?.Message ||
            error?.response?.data ||
            "Ocurrió un error al procesar la carga masiva",
          "error",
        );
      } finally {
        this.loadingDialog = false;
        this.resetImporter();
      }
    },
    resetImporter() {
      this.showImporter = false;
      this.$nextTick(() => {
        this.showImporter = true;
      });
    },
  },
};
</script>

<style scoped>
.gap-4 {
  gap: 16px;
}
</style>
